import React, { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetAdminDailyReportQuery,
  useGetAdminStatisticsQuery,
} from "../../slices/attendanceApiSlice";
import {
  useGetAcademyQuery,
  useGetDepartmentQuery,
  useGetDivisionsQuery,
} from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";
import LateReasonDialog from "../../components/LateReasonDialog/LateReasonDialog";
import LocationCell from "../../components/Attendance/LocationCell";
import "../../pages/css/Feedback.css";

export default function SuperAdminEmployeeAttendance() {
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("clocked_in");
  const [currentPage, setCurrentPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAcademyId, setSelectedAcademyId] = useState("all");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("all");
  const [showLateReasonDialog, setShowLateReasonDialog] = useState(false);
  const [selectedLateReason, setSelectedLateReason] = useState("");
  const pageSize = 20;

  // Fetch academies for the dropdown filter
  const { data: academiesData } = useGetAcademyQuery();
  const academies = academiesData || [];

  // Fetch departments - shared across all academies
  const { data: departmentsData } = useGetDepartmentQuery();
  const departments = departmentsData || [];

  // Fetch divisions - shared across all academies
  const { data: divisionsData } = useGetDivisionsQuery();
  const divisions = divisionsData || [];

  // Get today's date in YYYY-MM-DD format (local time)
  const today = new Date().toLocaleDateString("en-CA");

  // Map tab to status
  const statusMap = {
    clocked_in: "clocked_in",
    clocked_out: "clocked_out",
    absentees: "absent",
    on_leave: "on_leave",
  };

  // Reset page when academy filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [selectedAcademyId]);

  // Fetch admin daily report — academyId is sent to backend for server-side filtering
  const {
    data: reportData,
    isLoading,
    isFetching,
    error,
  } = useGetAdminDailyReportQuery({
    status: statusMap[activeTab],
    page: currentPage,
    size: pageSize,
    academyId: selectedAcademyId !== "all" ? selectedAcademyId : undefined,
  });

  // Get academy name by ID
  const getAcademyName = (academyId) => {
    if (!academyId || !academies.length) return "N/A";
    const academy = academies.find(
      (acad) => acad.academyId === academyId || acad._id === academyId,
    );
    return academy?.name || "N/A";
  };

  // Get department name by ID
  const getDepartmentName = (departmentId) => {
    if (!departmentId || !departments.length) return "N/A";
    const department = departments.find(
      (dept) => dept.departmentId === departmentId || dept._id === departmentId,
    );
    return department?.name || "N/A";
  };

  // Get division name by ID
  const getDivisionName = (divisionId) => {
    if (!divisionId || !divisions.length) return "N/A";
    const division = divisions.find(
      (div) => div.divisionId === divisionId || div._id === divisionId,
    );
    return division?.name || "N/A";
  };

  // Filter data based on search query, academy filter, department filter, and tab
  const filteredContent = useMemo(() => {
    if (!reportData?.content) return [];

    let filtered = reportData.content;

    // Filter by department (frontend only - doesn't affect API)
    if (selectedDepartmentId && selectedDepartmentId !== "all") {
      filtered = filtered.filter(
        (employee) => employee.departmentId === selectedDepartmentId,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employee) => {
        const fullName = `${employee.firstName || ""} ${
          employee.lastName || ""
        }`.toLowerCase();
        const email = (employee.email || "").toLowerCase();
        const employeeId = (employee.employeeId || "").toLowerCase();
        return (
          fullName.includes(query) ||
          email.includes(query) ||
          employeeId.includes(query)
        );
      });
    }

    // Extra safety: filter by tab semantics so rows don't leak between tabs
    filtered = filtered.filter((employee) => {
      if (activeTab === "clocked_in") {
        return !!employee.clockInTime;
      }
      if (activeTab === "clocked_out") {
        return !!employee.clockOutTime;
      }
      if (activeTab === "on_leave") {
        return !!employee.leaveDays;
      }
      if (activeTab === "absentees") {
        return (
          !employee.clockInTime && !employee.clockOutTime && !employee.leaveDays
        );
      }
      return true;
    });

    // Deduplicate by user per tab (avoid duplicates when API returns multiple)
    const uniqueMap = new Map();
    for (const emp of filtered) {
      const baseKey =
        emp.userId || emp.employeeId || emp.email || emp.attendanceId;
      if (!baseKey) continue;
      const key = `${activeTab}-${baseKey}`;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, emp);
      }
    }

    return Array.from(uniqueMap.values());
  }, [
    reportData,
    searchQuery,
    selectedAcademyId,
    selectedDepartmentId,
    academies,
    departments,
    activeTab,
  ]);

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) return "-";
    try {
      return new Date(timeString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Thimphu",
      });
    } catch {
      return timeString;
    }
  };

  // Get user initials for avatar
  const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return first + last || "U";
  };

  // Get full name
  const getFullName = (firstName, lastName) => {
    return `${firstName || ""} ${lastName || ""}`.trim() || "Unknown";
  };

  const handleBack = () => {
    navigate("/superadmin/attendance");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
    setSearchQuery("");
    setSelectedDepartmentId("all");
  };

  // Reset department filter when academy changes
  useEffect(() => {
    setSelectedDepartmentId("all");
  }, [selectedAcademyId]);

  // Prepare academy options for CustomSelect
  const academyOptions = useMemo(() => {
    return academies.map((academy) => ({
      value: academy.academyId || academy._id,
      label: academy.name,
      academyId: academy.academyId || academy._id,
      name: academy.name,
    }));
  }, [academies]);

  // Prepare department options for CustomSelect
  const departmentOptions = useMemo(() => {
    return departments.map((dept) => ({
      value: dept.departmentId || dept._id,
      label: dept.name,
      departmentId: dept.departmentId || dept._id,
      name: dept.name,
    }));
  }, [departments]);

  // Calculate pagination based on filtered content
  const totalFilteredElements = filteredContent.length;
  const totalPages = Math.ceil(totalFilteredElements / pageSize);
  const startIndex = currentPage * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedContent = filteredContent.slice(startIndex, endIndex);

  return (
    <div className="p-4 sm:py-6 sm:px-8 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-zinc-600 hover:bg-zinc-100 hover:text-zinc-800 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-zinc-800">
            Daily Employee Attendance
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-200 overflow-x-auto">
        <div className="flex space-x-4 sm:space-x-8 min-w-max">
          {[
            { id: "clocked_in", label: "Clocked In" },
            { id: "clocked_out", label: "Clocked Out" },
            { id: "absentees", label: "Absentees" },
            { id: "on_leave", label: "On Leave" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`pb-3 px-1 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-zinc-600 hover:text-zinc-800"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Employee Attendance Table View */}
      <>
        {/* Search and Filter Bar */}
        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-500 text-sm"
            />
          </div>
          {/* Academy Select */}
          <CustomSelect
            options={academyOptions}
            value={selectedAcademyId}
            onChange={setSelectedAcademyId}
            placeholder="Academy"
            allOptionLabel="All Academies"
            getDisplayValue={getAcademyName}
            allValue="all"
            minWidth={180}
          />

          {/* Department Select - Only show when academy is selected */}
          {selectedAcademyId !== "all" && (
            <CustomSelect
              options={departmentOptions}
              value={selectedDepartmentId}
              onChange={setSelectedDepartmentId}
              placeholder="Department"
              allOptionLabel="All Departments"
              getDisplayValue={getDepartmentName}
              allValue="all"
              minWidth={180}
            />
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-zinc-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider hidden sm:table-cell">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider hidden sm:table-cell">
                    Department
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider hidden sm:table-cell">
                    Division
                  </th>
                  {activeTab !== "absentees" && activeTab !== "on_leave" && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      Location
                    </th>
                  )}
                  {activeTab !== "absentees" && activeTab !== "on_leave" && (
                    <th className="px-6 py-3 text-left text-xs font-semibold text-zinc-700 uppercase tracking-wider">
                      {activeTab === "clocked_in"
                        ? "Clocked in at"
                        : "Clocked out at"}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-zinc-200">
                {isLoading || isFetching ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`skeleton-${idx}`}>
                      <td
                        colSpan={
                          activeTab !== "absentees" && activeTab !== "on_leave"
                            ? 6
                            : 4
                        }
                        className="px-6 py-4"
                      >
                        <div className="animate-pulse flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full bg-zinc-200" />
                          <div className="flex-1 space-y-2">
                            <div className="h-3 bg-zinc-200 rounded w-1/3" />
                            <div className="h-3 bg-zinc-200 rounded w-1/4" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : error ? (
                  <tr>
                    <td
                      colSpan={activeTab === "absentees" ? 5 : 6}
                      className="px-6 py-8 text-center text-red-500"
                    >
                      Error loading data. Please try again.
                    </td>
                  </tr>
                ) : filteredContent.length === 0 ? (
                  <tr>
                    <td
                      colSpan={activeTab === "absentees" ? 5 : 6}
                      className="px-6 py-8 text-center text-zinc-500"
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  paginatedContent.map((employee) => (
                    <tr
                      key={employee.userId}
                      className="hover:bg-zinc-50 cursor-pointer"
                      onClick={() => {
                        if (employee.attendanceId) {
                          navigate(
                            `/superadmin/attendance/record/${employee.attendanceId}`,
                          );
                        }
                      }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="relative inline-block">
                            {employee.profileImageUrl ? (
                              <img
                                src={employee.profileImageUrl}
                                alt={getFullName(
                                  employee.firstName,
                                  employee.lastName,
                                )}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 rounded-full bg-zinc-300 flex items-center justify-center">
                                <span className="text-zinc-600 font-semibold text-sm">
                                  {getInitials(
                                    employee.firstName,
                                    employee.lastName,
                                  )}
                                </span>
                              </div>
                            )}
                            {employee.lateReason && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedLateReason(employee.lateReason);
                                  setShowLateReasonDialog(true);
                                }}
                                className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded-full hover:bg-orange-100 transition-colors shadow-sm whitespace-nowrap z-10"
                                title="Late arrival"
                              >
                                <Clock size={12} />
                                Late
                              </button>
                            )}
                          </div>
                          {/* Mobile: show name, department, division stacked next to avatar */}
                          <div className="ml-3 sm:hidden">
                            <div className="text-sm font-medium text-zinc-900">
                              {getFullName(
                                employee.firstName,
                                employee.lastName,
                              )}
                            </div>
                            <div className="text-xs text-zinc-500">
                              {getDepartmentName(employee.departmentId)}
                            </div>
                            <div className="text-xs text-zinc-500">
                              {getDivisionName(employee.divisionId)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm font-medium text-zinc-900">
                          {getFullName(employee.firstName, employee.lastName)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-zinc-600">
                          {getDepartmentName(employee.departmentId)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                        <div className="text-sm text-zinc-600">
                          {getDivisionName(employee.divisionId)}
                        </div>
                      </td>
                      {activeTab !== "absentees" &&
                        activeTab !== "on_leave" && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <LocationCell
                              location={
                                activeTab === "clocked_in"
                                  ? employee.clockInLocation
                                  : activeTab === "clocked_out"
                                    ? employee.clockOutLocation
                                    : null
                              }
                            />
                          </td>
                        )}
                      {activeTab !== "absentees" &&
                        activeTab !== "on_leave" && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-zinc-600">
                              {activeTab === "clocked_in"
                                ? formatTime(employee.clockInTime)
                                : formatTime(employee.clockOutTime)}
                            </div>
                          </td>
                        )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 7) {
                      pageNum = i;
                    } else if (currentPage <= 3) {
                      pageNum = i;
                    } else if (currentPage >= totalPages - 4) {
                      pageNum = totalPages - 7 + i;
                    } else {
                      pageNum = currentPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setCurrentPage(pageNum)}
                        className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                          currentPage === pageNum
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-600 hover:bg-zinc-100"
                        }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                  {totalPages > 7 && currentPage < totalPages - 4 && (
                    <>
                      <span className="px-2 text-zinc-400">...</span>
                      <button
                        onClick={() => setCurrentPage(totalPages - 1)}
                        className="px-3 py-1 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 transition-colors"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage === totalPages - 1}
                  className="p-2 rounded-lg border border-zinc-300 hover:bg-zinc-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="text-sm text-zinc-600 font-medium">
                {totalFilteredElements} Results
              </div>
            </div>
          )}
        </div>
      </>
      <LateReasonDialog
        show={showLateReasonDialog}
        onClose={() => {
          setShowLateReasonDialog(false);
          setSelectedLateReason("");
        }}
        lateReason={selectedLateReason}
      />
    </div>
  );
}
